// frontend/src/components/DegreeAudit/HierarchicalDegreeAudit.tsx
// v2.4 - Added drag-and-drop reordering for programs

import { useState, useCallback, useMemo } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { useDegreeAuditData } from '../../hooks/useDegreeAuditData';
import { useExpansionState } from '../../hooks/useExpansionState';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { typedApi } from '../../services/typedApiClient';
import { useInstitutionConfig } from '../../contexts/InstitutionConfigContext';

import HierarchicalSkeleton from './Common/LoadingSkeletons';
import ConstraintViolations, {
  type ConstraintViolationsData,
} from './Common/ConstraintViolations';
import ConstraintWarnings, {
  type ConstraintWarning,
} from './Common/ConstraintWarnings';
import GraduationGates, {
  type GraduationReadiness,
} from './Common/GraduationGates';
import DebugSection from './Common/DebugSection';
import NoMajorDeclared from './Common/NoMajorDeclared';
import DegreeRequirementsHeader from './DegreeRequirementsHeader';
import { HierarchicalProgramModal } from '../shared/modals';
import SortableProgramList, { type ProgramData } from './SortableProgramList';
import { PlanOverridesProvider } from '../../contexts/PlanOverridesContext';
import { isGenEdProgram as isGenEdProgramShared } from '@/utils/programClassification';

type ProgramTypeLiteral = 'major' | 'minor' | 'citation';

interface HierarchicalDegreeAuditPlan {
  id?: number;
  student_name?: string;
  name?: string;
  credit_target?: number;
  majors?: Array<{ program?: string; concentration_department?: string }>;
  gpa?:
    | { major_gpas?: Record<string, { current?: number | null; projected?: number | null }> }
    | null;
  [key: string]: unknown;
}

export interface HierarchicalDegreeAuditProps {
  hasAcademicPlan: boolean;
  academicPlan: HierarchicalDegreeAuditPlan | null | undefined;
  chatDebugInfo?: unknown;
  onManageSemesters?: () => void;
  onDismissSetup?: () => void;
}

const HierarchicalDegreeAudit = ({
  hasAcademicPlan,
  academicPlan,
  chatDebugInfo,
  onManageSemesters,
  onDismissSetup,
}: HierarchicalDegreeAuditProps) => {
  const [setupDismissed, setSetupDismissed] = useState(
    localStorage.getItem('academic_planner_setup_dismissed') === 'true'
  );

  const {
    hierarchicalData,
    loading: hierarchicalLoading,
    authError,
    refresh: refreshDegreeAudit,
  } = useDegreeAuditData(academicPlan?.id ?? null);

  const { expandedNodes, toggleNode, toggleProgramWithChildren } = useExpansionState(
    hierarchicalData,
    false
  );

  const isUnivReqProgram = (p: ProgramData): boolean =>
    p.program === 'University Requirements' ||
    p.program_key === 'UNIVERSITY_REQUIREMENTS' ||
    p.degree_type === 'UR';

  // UX-R1 task 2.1: General Education is auto-managed (degree-wide), not a
  // user-chosen program — it renders in the "Degree Requirements" group under
  // the header card (alongside University Requirements), not among majors.
  // Predicate: utils/programClassification (one copy for the whole UI,
  // including degree_type='GE' — the backend classification, which covers
  // every institution's named GE tracks).
  const isGenEdProgram = (p: ProgramData): boolean => isGenEdProgramShared(p);

  const universityRequirements = useMemo(() => {
    const majors = (hierarchicalData?.majors as ProgramData[] | undefined) || [];
    return majors.find(isUnivReqProgram) || null;
  }, [hierarchicalData]);

  const genEdPrograms = useMemo(() => {
    const majors = (hierarchicalData?.majors as ProgramData[] | undefined) || [];
    return majors.filter((m) => !isUnivReqProgram(m) && isGenEdProgram(m));
  }, [hierarchicalData]);

  const displayMajors = useMemo(() => {
    const majors = (hierarchicalData?.majors as ProgramData[] | undefined) || [];
    return majors.filter((m) => !isUnivReqProgram(m) && !isGenEdProgram(m));
  }, [hierarchicalData]);

  // Phase 5 pre-health advising overlays (warning-tier, no reorder).
  const advisingOverlays =
    ((hierarchicalData as { advising_overlays?: unknown })?.advising_overlays as
      | ProgramData[]
      | undefined) ?? [];

  // PR #45 review follow-up: ONE source of truth for "user-chosen content
  // exists" covering every list rendered under the "Your Programs" heading
  // (majors, minors, citations, advising overlays) — so the heading guard
  // can't drift out of sync with the list set again.
  const hasUserPrograms =
    displayMajors.length > 0 ||
    ((hierarchicalData?.minors as ProgramData[] | undefined)?.length ?? 0) > 0 ||
    ((hierarchicalData?.citations as ProgramData[] | undefined)?.length ?? 0) > 0 ||
    advisingOverlays.length > 0;

  const [showManageModal, setShowManageModal] = useState(false);

  const { user } = useAuth();
  const { showError } = useToast();

  const handleReorderPrograms = useCallback(
    async (programType: ProgramTypeLiteral, newPrograms: ProgramData[]): Promise<void> => {
      if (!academicPlan?.id || !user?.id) {
        showError('Unable to reorder: plan or user not available');
        return;
      }

      try {
        const simplifiedPrograms = newPrograms.map((p) => ({
          program: p.program ?? '',
          track: p.track || 'General',
        }));

        const updates =
          programType === 'major'
            ? { majors: simplifiedPrograms }
            : programType === 'minor'
              ? { minors: simplifiedPrograms }
              : { citations: simplifiedPrograms };

        await typedApi.academicPlans.reorderPrograms(academicPlan.id, updates);

        refreshDegreeAudit();
      } catch (err) {
        console.error('Error reordering programs:', err);
        showError('Failed to reorder programs');
      }
    },
    [academicPlan?.id, user?.id, refreshDegreeAudit, showError]
  );

  const handleReorderMajors = useCallback(
    (newMajors: ProgramData[]) => {
      handleReorderPrograms('major', newMajors);
    },
    [handleReorderPrograms]
  );

  const handleReorderMinors = useCallback(
    (newMinors: ProgramData[]) => {
      handleReorderPrograms('minor', newMinors);
    },
    [handleReorderPrograms]
  );

  const handleReorderCitations = useCallback(
    (newCitations: ProgramData[]) => {
      handleReorderPrograms('citation', newCitations);
    },
    [handleReorderPrograms]
  );

  const [optimisticCreditTarget, setOptimisticCreditTarget] = useState<number | null>(null);
  // W49 A3: institution graduation target, not the 120 literal.
  const { config: institutionConfig } = useInstitutionConfig();
  const effectiveCreditTarget =
    optimisticCreditTarget ??
    academicPlan?.credit_target ??
    institutionConfig.graduationCredits;

  const handleCreditTargetChange = useCallback(
    async (creditTarget: number): Promise<void> => {
      if (!academicPlan?.id) return;
      setOptimisticCreditTarget(creditTarget);
      try {
        await typedApi.academicPlans.updateCreditTarget(academicPlan.id, creditTarget);
        refreshDegreeAudit();
      } catch (err) {
        console.error('Error updating credit target:', err);
        showError('Failed to update credit target');
        setOptimisticCreditTarget(null);
      }
    },
    [academicPlan?.id, refreshDegreeAudit, showError]
  );

  const handleConcentrationChange = useCallback(
    async (programName: string, department: string): Promise<void> => {
      if (!academicPlan?.id) return;
      try {
        await typedApi.academicPlans.update(academicPlan.id, 'update_concentration', {
          program_name: programName,
          department,
        });
        refreshDegreeAudit();
      } catch (err) {
        console.error('Error updating concentration:', err);
        showError('Failed to update concentration department');
      }
    },
    [academicPlan?.id, refreshDegreeAudit, showError]
  );

  const concentrationDepartments = useMemo(() => {
    const depts: Record<string, string> = {};
    if (academicPlan?.majors) {
      for (const major of academicPlan.majors) {
        if (major.concentration_department && major.program) {
          depts[major.program] = major.concentration_department;
        }
      }
    }
    return depts;
  }, [academicPlan?.majors]);

  const hasAnyPrograms = (): boolean => {
    const hasMajors = !!hierarchicalData?.majors && hierarchicalData.majors.length > 0;
    const hasMinors = !!hierarchicalData?.minors && hierarchicalData.minors.length > 0;
    return hasMajors || hasMinors;
  };

  const getUserName = (): string =>
    academicPlan?.student_name || academicPlan?.name || 'Student';

  const handleDismissSetup = (): void => {
    localStorage.setItem('academic_planner_setup_dismissed', 'true');
    setSetupDismissed(true);
    if (onDismissSetup) {
      onDismissSetup();
    }
  };

  if (hierarchicalLoading) {
    return (
      <div className="space-y-4">
        <HierarchicalSkeleton />
      </div>
    );
  }

  if (!hasAcademicPlan) {
    return (
      <div className="p-6 md:p-8 text-center">
        <div className="text-gray-500">
          <p className="text-sm md:text-base">No academic plan available.</p>
          <p className="text-xs md:text-sm mt-2">
            Please create or select an academic plan to view degree requirements.
          </p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="p-6 md:p-8 text-center">
        <div className="max-w-md mx-auto">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
            Authentication Required
          </h3>

          <p className="text-sm md:text-base text-gray-600 mb-6">{authError}</p>

          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <PlanOverridesProvider planId={academicPlan?.id ?? null} onMutate={refreshDegreeAudit}>
    <div className="print-degree-audit space-y-3">
      <h2 className="print-section-title hidden print:block">Degree Requirements</h2>

      <DegreeRequirementsHeader
        hierarchicalData={hierarchicalData}
        academicPlan={academicPlan}
        creditTarget={effectiveCreditTarget}
        onOpenManageModal={() => setShowManageModal(true)}
        onRefreshData={refreshDegreeAudit}
        onCreditTargetChange={handleCreditTargetChange}
        universityRequirements={universityRequirements}
        expandedNodes={expandedNodes}
        toggleNode={toggleNode}
      />

      {/* UX-R1 2.1: auto-managed General Education sits in the Degree
          Requirements group, directly under the header card (which already
          hosts University Requirements) — not among the user's programs. */}
      {genEdPrograms.length > 0 && (
        <SortableProgramList
          programs={genEdPrograms}
          programType="gened"
          expandedNodes={expandedNodes}
          toggleNode={toggleNode}
          toggleProgramWithChildren={toggleProgramWithChildren}
          onReorder={() => {}}
        />
      )}

      {hierarchicalLoading && hierarchicalData && (
        <div className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Updating requirements...</span>
        </div>
      )}

      <div className="space-y-4 md:space-y-6">
        <ConstraintViolations
          violations={hierarchicalData?.constraint_violations as ConstraintViolationsData}
        />

        {/* Graduation-readiness gates (credits / residency / GPA) as
            neutral progress; replaces the always-on residency warning. */}
        <GraduationGates
          readiness={
            (hierarchicalData as { graduation_readiness?: GraduationReadiness } | undefined)
              ?.graduation_readiness
          }
        />

        {/* W22 §22.7 — soft warnings from Phase 4 inert-constraint readers. */}
        <ConstraintWarnings
          warnings={
            (hierarchicalData as { warnings?: ConstraintWarning[] } | undefined)?.warnings
          }
        />

        {hasAnyPrograms() ? (
          <div className="space-y-6 md:space-y-8">
            {/* UX-R1 2.1: label the user-chosen programs to separate them
                from the auto-managed Degree Requirements group above.
                PR #44/#45 review follow-ups: gate on hasUserPrograms — the
                single predicate covering all four lists below —
                hasAnyPrograms() counts auto-managed GE/UR (which render
                above) and would orphan the heading. */}
            {hasUserPrograms && (
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-3 -mb-3 md:-mb-5">
                Your Programs
              </h3>
            )}
            {displayMajors.length > 0 && (
              <SortableProgramList
                programs={displayMajors}
                programType="major"
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                toggleProgramWithChildren={toggleProgramWithChildren}
                onReorder={handleReorderMajors}
                majorGpas={academicPlan?.gpa?.major_gpas}
                concentrationDepartments={concentrationDepartments}
                onConcentrationChange={handleConcentrationChange}
              />
            )}

            {hierarchicalData?.minors && hierarchicalData.minors.length > 0 && (
              <SortableProgramList
                programs={hierarchicalData.minors as ProgramData[]}
                programType="minor"
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                toggleProgramWithChildren={toggleProgramWithChildren}
                onReorder={handleReorderMinors}
              />
            )}

            {hierarchicalData?.citations && hierarchicalData.citations.length > 0 && (
              <SortableProgramList
                programs={hierarchicalData.citations as ProgramData[]}
                programType="citation"
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                toggleProgramWithChildren={toggleProgramWithChildren}
                onReorder={handleReorderCitations}
              />
            )}

            {/* Phase 5: pre-health advising overlays — warning-tier, no reorder
                (the standing "confirm with advising" disclaimer lives in the card). */}
            {advisingOverlays.length > 0 && (
              <SortableProgramList
                programs={advisingOverlays}
                programType="advising"
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                toggleProgramWithChildren={toggleProgramWithChildren}
                onReorder={() => {}}
              />
            )}
          </div>
        ) : setupDismissed ? (
          <div className="text-center py-8">
            <div className="max-w-md mx-auto">
              <p className="text-gray-600 mb-4">No degree programs selected yet.</p>
              <button
                onClick={() => setShowManageModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Select Programs
              </button>
            </div>
          </div>
        ) : (
          <NoMajorDeclared
            onOpenManageModal={() => setShowManageModal(true)}
            onManageSemesters={onManageSemesters}
            academicPlan={academicPlan}
            userName={getUserName()}
            onDismissSetup={handleDismissSetup}
          />
        )}

        {!!chatDebugInfo && (
          <DebugSection
            hierarchicalData={hierarchicalData}
            academicPlan={academicPlan}
            chatDebugInfo={chatDebugInfo}
          />
        )}
      </div>

      <HierarchicalProgramModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        hierarchicalData={hierarchicalData}
        academicPlan={academicPlan}
        onProgramsUpdated={refreshDegreeAudit}
      />
    </div>
    </PlanOverridesProvider>
  );
};

export default HierarchicalDegreeAudit;
